import React, { useState, useEffect } from 'react';
import { hospitalApi, paymentApi } from '../services/api';

type Currency = 'USD' | 'NGN' | 'EUR' | 'GBP';

const CURRENCY_SYMBOLS: Record<Currency, string> = {
  USD: '$',
  NGN: '₦',
  EUR: '€',
  GBP: '£',
};

const CURRENCY_NAMES: Record<Currency, string> = {
  USD: 'US Dollar',
  NGN: 'Nigerian Naira',
  EUR: 'Euro',
  GBP: 'British Pound',
};

interface Hospital {
  hospitalId: string;
  name: string;
  address?: string;
  contactEmail?: string;
  contactPhone?: string;
  subscriptionAmount: number;
  currency: Currency;
  registrationDate: string;
  isActive: boolean;
  totalPaid: number;
  // Device fields
  hasDevice?: boolean;
  deviceDescription?: string;
  deviceContractAmount?: number;
  deviceMonthlyPayment?: number;
  deviceCurrency?: Currency;
  deviceTotalPaid?: number;
  // Payment status override
  paymentStatusOverride?: 'paid' | 'partial' | 'pending' | 'overdue' | null;
}

interface Payment {
  paymentId: string;
  hospitalId: string;
  amount: number;
  month: string;
  paymentDate: string;
}

type PaymentStatus = 'paid' | 'partial' | 'pending' | 'overdue';

// Get current month in YYYY-MM format
const getCurrentMonth = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

// Get last month in YYYY-MM format
const getLastMonth = (): string => {
  const now = new Date();
  now.setMonth(now.getMonth() - 1);
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

// Calculate payment status for a hospital
const calculatePaymentStatus = (
  hospital: Hospital,
  payments: Payment[],
  currentMonth: string,
  lastMonth: string
): PaymentStatus => {
  // Check for manual override
  if (hospital.paymentStatusOverride) {
    return hospital.paymentStatusOverride;
  }

  const currentMonthPayments = payments.filter(
    p => p.hospitalId === hospital.hospitalId && p.month === currentMonth
  );
  
  const lastMonthPayments = payments.filter(
    p => p.hospitalId === hospital.hospitalId && p.month === lastMonth
  );

  const totalPaidThisMonth = currentMonthPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const totalPaidLastMonth = lastMonthPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const monthlyDue = hospital.subscriptionAmount || 0;

  // Check if full payment received this month
  if (totalPaidThisMonth >= monthlyDue) {
    return 'paid';
  }

  // Check if partial payment received this month
  if (totalPaidThisMonth > 0) {
    return 'partial';
  }

  // Check if last month was not paid (overdue)
  if (totalPaidLastMonth < monthlyDue) {
    return 'overdue';
  }

  // No payment this month yet (pending)
  return 'pending';
};

// Payment status badge configuration
const PAYMENT_STATUS_CONFIG: Record<PaymentStatus, { icon: string; label: string; bgColor: string; textColor: string; borderColor: string }> = {
  paid: { icon: '✓', label: 'Paid', bgColor: '#dcfce7', textColor: '#166534', borderColor: '#86efac' },
  partial: { icon: '◐', label: 'Partial', bgColor: '#fef3c7', textColor: '#92400e', borderColor: '#fcd34d' },
  pending: { icon: '○', label: 'Pending', bgColor: '#fef9c3', textColor: '#854d0e', borderColor: '#fde047' },
  overdue: { icon: '!', label: 'Overdue', bgColor: '#fee2e2', textColor: '#991b1b', borderColor: '#fca5a5' },
};

const Hospitals: React.FC = () => {
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingHospital, setEditingHospital] = useState<Hospital | null>(null);
  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentHospital, setPaymentHospital] = useState<Hospital | null>(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMonth, setPaymentMonth] = useState(getCurrentMonth());
  const [savingPayment, setSavingPayment] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    contactEmail: '',
    contactPhone: '',
    subscriptionAmount: '',
    currency: 'USD' as Currency,
    // Device fields
    hasDevice: false,
    deviceDescription: '',
    deviceContractAmount: '',
    deviceMonthlyPayment: '',
    deviceCurrency: 'USD' as Currency,
  });
  const [saving, setSaving] = useState(false);

  // Pre-calculate month values
  const currentMonth = getCurrentMonth();
  const lastMonth = getLastMonth();

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [hospitalsData, paymentsData] = await Promise.all([
        hospitalApi.getAll(),
        paymentApi.getAll(),
      ]);
      setHospitals(hospitalsData);
      setPayments(paymentsData);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadHospitals = async () => {
    await loadData();
  };

  // Handler to update payment status (manual override)
  const handlePaymentStatusChange = async (hospital: Hospital, newStatus: PaymentStatus | 'auto') => {
    try {
      const override = newStatus === 'auto' ? null : newStatus;
      await hospitalApi.update(hospital.hospitalId, {
        paymentStatusOverride: override,
      });
      // Update local state
      setHospitals(prev => 
        prev.map(h => 
          h.hospitalId === hospital.hospitalId 
            ? { ...h, paymentStatusOverride: override }
            : h
        )
      );
    } catch (err) {
      console.error('Error updating payment status:', err);
      alert('Failed to update payment status.');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const openAddModal = () => {
    setEditingHospital(null);
    setFormData({
      name: '',
      address: '',
      contactEmail: '',
      contactPhone: '',
      subscriptionAmount: '',
      currency: 'USD',
      hasDevice: false,
      deviceDescription: '',
      deviceContractAmount: '',
      deviceMonthlyPayment: '',
      deviceCurrency: 'USD',
    });
    setShowModal(true);
  };

  const openEditModal = (hospital: Hospital) => {
    setEditingHospital(hospital);
    setFormData({
      name: hospital.name,
      address: hospital.address || '',
      contactEmail: hospital.contactEmail || '',
      contactPhone: hospital.contactPhone || '',
      subscriptionAmount: hospital.subscriptionAmount.toString(),
      currency: hospital.currency || 'USD',
      hasDevice: hospital.hasDevice || false,
      deviceDescription: hospital.deviceDescription || '',
      deviceContractAmount: hospital.deviceContractAmount?.toString() || '',
      deviceMonthlyPayment: hospital.deviceMonthlyPayment?.toString() || '',
      deviceCurrency: hospital.deviceCurrency || 'USD',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.subscriptionAmount) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      
      const hospitalData = {
        name: formData.name,
        address: formData.address || undefined,
        contactEmail: formData.contactEmail || undefined,
        contactPhone: formData.contactPhone || undefined,
        subscriptionAmount: parseFloat(formData.subscriptionAmount),
        currency: formData.currency,
        hasDevice: formData.hasDevice,
        deviceDescription: formData.hasDevice ? formData.deviceDescription : undefined,
        deviceContractAmount: formData.hasDevice && formData.deviceContractAmount ? parseFloat(formData.deviceContractAmount) : undefined,
        deviceMonthlyPayment: formData.hasDevice && formData.deviceMonthlyPayment ? parseFloat(formData.deviceMonthlyPayment) : undefined,
        deviceCurrency: formData.hasDevice ? formData.deviceCurrency : undefined,
      };
      
      if (editingHospital) {
        await hospitalApi.update(editingHospital.hospitalId, hospitalData);
      } else {
        await hospitalApi.create(hospitalData);
      }
      
      setShowModal(false);
      loadHospitals();
    } catch (err) {
      console.error('Error saving hospital:', err);
      alert('Failed to save hospital. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const toggleHospitalStatus = async (hospital: Hospital) => {
    try {
      await hospitalApi.update(hospital.hospitalId, {
        isActive: !hospital.isActive,
      });
      loadHospitals();
    } catch (err) {
      console.error('Error updating hospital status:', err);
      alert('Failed to update hospital status.');
    }
  };

  const handleDeleteHospital = async (hospital: Hospital) => {
    if (!window.confirm(`Delete "${hospital.name}"? This cannot be undone.`)) return;
    try {
      await hospitalApi.delete(hospital.hospitalId);
      loadHospitals();
    } catch (err) {
      console.error('Error deleting hospital:', err);
      alert('Failed to delete hospital. Please try again.');
    }
  };

  // Open payment modal
  const openPaymentModal = (hospital: Hospital) => {
    setPaymentHospital(hospital);
    setPaymentAmount(hospital.subscriptionAmount.toString());
    setPaymentMonth(getCurrentMonth());
    setShowPaymentModal(true);
  };

  // Handle payment submission
  const handlePaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!paymentHospital || !paymentAmount || !paymentMonth) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setSavingPayment(true);
      
      // Use the first day of the selected month as the payment date
      const paymentDate = `${paymentMonth}-01`;
      
      await paymentApi.create({
        hospitalId: paymentHospital.hospitalId,
        amount: parseFloat(paymentAmount),
        month: paymentMonth,
        paymentDate: paymentDate,
      });
      
      setShowPaymentModal(false);
      setPaymentAmount('');
      loadData(); // Reload to update totals
    } catch (err) {
      console.error('Error saving payment:', err);
      alert('Failed to save payment. Please try again.');
    } finally {
      setSavingPayment(false);
    }
  };

  const formatCurrency = (amount: number, currency: Currency = 'USD') => {
    const symbol = CURRENCY_SYMBOLS[currency] || '$';
    return `${symbol}${new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)}`;
  };

  const formatDate = (dateString: string) => {
    // Parse date string as local time to avoid timezone shift
    // "2026-01-01" should show as "Jan 1, 2026" not "Dec 31, 2025"
    const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="hospitals-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading hospitals...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="hospitals-page">
      <div className="page-header">
        <div>
          <h2>Hospital Management</h2>
          <p className="page-subtitle">Manage hospital registrations and subscriptions</p>
        </div>
        <div className="header-actions">
          <button className="btn-refresh" onClick={loadHospitals}>
            🔄 Refresh
          </button>
          <button className="btn-primary" onClick={openAddModal}>
            + Add Hospital
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <p>{error}</p>
          <button onClick={loadHospitals}>Try Again</button>
        </div>
      )}

      <div className="hospitals-table-container">
        <table className="hospitals-table">
          <thead>
            <tr>
              <th>Hospital Name</th>
              <th>Contact</th>
              <th>Subscription</th>
              <th>Payment</th>
              <th>Total Paid</th>
              <th>Registered</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {hospitals.length === 0 ? (
              <tr>
                <td colSpan={8} className="empty-row">
                  <div className="empty-state">
                    <p>No hospitals registered yet.</p>
                    <button className="btn-primary" onClick={openAddModal}>
                      Add First Hospital
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              hospitals.map((hospital) => (
                <tr key={hospital.hospitalId}>
                  <td>
                    <div className="hospital-name-cell">
                      <span className="hospital-name">{hospital.name}</span>
                      {hospital.address && (
                        <span className="hospital-address">{hospital.address}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="contact-cell">
                      {hospital.contactEmail && (
                        <span className="contact-email">{hospital.contactEmail}</span>
                      )}
                      {hospital.contactPhone && (
                        <span className="contact-phone">{hospital.contactPhone}</span>
                      )}
                      {!hospital.contactEmail && !hospital.contactPhone && (
                        <span className="no-contact">No contact info</span>
                      )}
                    </div>
                  </td>
                  <td className="amount-cell">
                    {formatCurrency(hospital.subscriptionAmount, hospital.currency)}/mo
                    <span className="currency-badge">{hospital.currency || 'USD'}</span>
                  </td>
                  <td className="payment-status-cell">
                    {(() => {
                      const status = calculatePaymentStatus(hospital, payments, currentMonth, lastMonth);
                      const config = PAYMENT_STATUS_CONFIG[status];
                      const monthPayments = payments.filter(
                        p => p.hospitalId === hospital.hospitalId && p.month === currentMonth
                      );
                      const paidThisMonth = monthPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
                      return (
                        <div className="payment-status-wrapper">
                          <div className="payment-status-dropdown">
                            <select
                              className="payment-status-select"
                              value={hospital.paymentStatusOverride || 'auto'}
                              onChange={(e) => handlePaymentStatusChange(hospital, e.target.value as PaymentStatus | 'auto')}
                              style={{
                                backgroundColor: config.bgColor,
                                color: config.textColor,
                                borderColor: config.borderColor,
                              }}
                            >
                              <option value="auto" style={{ backgroundColor: config.bgColor }}>
                                {config.icon} {config.label}
                              </option>
                              <option value="paid" style={{ backgroundColor: '#dcfce7', color: '#166534' }}>
                                ✓ Paid
                              </option>
                              <option value="partial" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
                                ◐ Partial
                              </option>
                              <option value="pending" style={{ backgroundColor: '#fef9c3', color: '#854d0e' }}>
                                ○ Pending
                              </option>
                              <option value="overdue" style={{ backgroundColor: '#fee2e2', color: '#991b1b' }}>
                                ! Overdue
                              </option>
                            </select>
                          </div>
                          <button
                            className="btn-add-payment"
                            onClick={() => openPaymentModal(hospital)}
                            title="Add Payment"
                          >
                            💳+
                          </button>
                          {paidThisMonth > 0 && (
                            <span className="month-paid-amount">
                              {formatCurrency(paidThisMonth, hospital.currency)} this month
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="amount-cell total-paid">
                    <div>
                      <span>{formatCurrency(hospital.totalPaid || 0, hospital.currency)}</span>
                      {hospital.hasDevice && hospital.deviceTotalPaid ? (
                        <span className="device-total">
                          +{formatCurrency(hospital.deviceTotalPaid, hospital.deviceCurrency)} (device)
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td>{formatDate(hospital.registrationDate)}</td>
                  <td>
                    <span className={`status-badge ${hospital.isActive ? 'active' : 'inactive'}`}>
                      {hospital.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon edit"
                        onClick={() => openEditModal(hospital)}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        className={`btn-icon ${hospital.isActive ? 'deactivate' : 'activate'}`}
                        onClick={() => toggleHospitalStatus(hospital)}
                        title={hospital.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {hospital.isActive ? '⏸️' : '▶️'}
                      </button>
                      <button
                        className="btn-icon delete"
                        onClick={() => handleDeleteHospital(hospital)}
                        title="Delete Hospital"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && paymentHospital && (
        <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="modal modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>💳 Record Payment</h3>
              <button className="modal-close" onClick={() => setShowPaymentModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handlePaymentSubmit}>
              <div className="payment-hospital-info">
                <span className="hospital-name-display">{paymentHospital.name}</span>
                <span className="subscription-info">
                  Subscription: {formatCurrency(paymentHospital.subscriptionAmount, paymentHospital.currency)}/mo
                </span>
              </div>
              
              <div className="form-group">
                <label htmlFor="paymentMonth">Month *</label>
                <input
                  type="month"
                  id="paymentMonth"
                  value={paymentMonth}
                  onChange={(e) => setPaymentMonth(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="paymentAmountInput">Amount ({paymentHospital.currency}) *</label>
                <input
                  type="number"
                  id="paymentAmountInput"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  required
                  min="0"
                  step="0.01"
                  placeholder="Enter amount"
                />
                <div className="quick-amount-buttons">
                  <button
                    type="button"
                    className="quick-btn"
                    onClick={() => setPaymentAmount(paymentHospital.subscriptionAmount.toString())}
                  >
                    Full ({formatCurrency(paymentHospital.subscriptionAmount, paymentHospital.currency)})
                  </button>
                  <button
                    type="button"
                    className="quick-btn"
                    onClick={() => setPaymentAmount((paymentHospital.subscriptionAmount / 2).toString())}
                  >
                    Half ({formatCurrency(paymentHospital.subscriptionAmount / 2, paymentHospital.currency)})
                  </button>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowPaymentModal(false)}
                  disabled={savingPayment}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={savingPayment}>
                  {savingPayment ? 'Saving...' : 'Record Payment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingHospital ? 'Edit Hospital' : 'Add New Hospital'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-section">
                <h4>Hospital Information</h4>
                <div className="form-group">
                  <label htmlFor="name">Hospital Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter hospital name"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="address">Address</label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Enter address"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="contactEmail">Email</label>
                    <input
                      type="email"
                      id="contactEmail"
                      name="contactEmail"
                      value={formData.contactEmail}
                      onChange={handleInputChange}
                      placeholder="contact@hospital.com"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="contactPhone">Phone</label>
                    <input
                      type="tel"
                      id="contactPhone"
                      name="contactPhone"
                      value={formData.contactPhone}
                      onChange={handleInputChange}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h4>Subscription Details</h4>
                <div className="form-row">
                  <div className="form-group flex-2">
                    <label htmlFor="subscriptionAmount">Monthly Subscription Amount *</label>
                    <input
                      type="number"
                      id="subscriptionAmount"
                      name="subscriptionAmount"
                      value={formData.subscriptionAmount}
                      onChange={handleInputChange}
                      required
                      min="0"
                      step="0.01"
                      placeholder="Enter amount"
                    />
                  </div>
                  <div className="form-group flex-1">
                    <label htmlFor="currency">Currency *</label>
                    <select
                      id="currency"
                      name="currency"
                      value={formData.currency}
                      onChange={handleInputChange}
                      className="currency-select"
                    >
                      {(Object.keys(CURRENCY_SYMBOLS) as Currency[]).map(curr => (
                        <option key={curr} value={curr}>
                          {CURRENCY_SYMBOLS[curr]} {curr} - {CURRENCY_NAMES[curr]}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h4>Device Contract (Optional)</h4>
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="hasDevice"
                      checked={formData.hasDevice}
                      onChange={handleInputChange}
                    />
                    <span>Hospital has a device contract with us</span>
                  </label>
                </div>

                {formData.hasDevice && (
                  <>
                    <div className="form-group">
                      <label htmlFor="deviceDescription">Device Description</label>
                      <textarea
                        id="deviceDescription"
                        name="deviceDescription"
                        value={formData.deviceDescription}
                        onChange={handleInputChange}
                        placeholder="Describe the devices provided (e.g., 2x Medical Scanners, 1x Server)"
                        rows={2}
                      />
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="deviceContractAmount">Total Contract Amount</label>
                        <input
                          type="number"
                          id="deviceContractAmount"
                          name="deviceContractAmount"
                          value={formData.deviceContractAmount}
                          onChange={handleInputChange}
                          min="0"
                          step="0.01"
                          placeholder="Total contract value"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="deviceMonthlyPayment">Monthly Payment</label>
                        <input
                          type="number"
                          id="deviceMonthlyPayment"
                          name="deviceMonthlyPayment"
                          value={formData.deviceMonthlyPayment}
                          onChange={handleInputChange}
                          min="0"
                          step="0.01"
                          placeholder="Monthly payment"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="deviceCurrency">Currency</label>
                        <select
                          id="deviceCurrency"
                          name="deviceCurrency"
                          value={formData.deviceCurrency}
                          onChange={handleInputChange}
                          className="currency-select"
                        >
                          {(Object.keys(CURRENCY_SYMBOLS) as Currency[]).map(curr => (
                            <option key={curr} value={curr}>
                              {CURRENCY_SYMBOLS[curr]} {curr}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowModal(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editingHospital ? 'Update Hospital' : 'Add Hospital'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .form-section {
          margin-bottom: 24px;
          padding-bottom: 16px;
          border-bottom: 1px solid #e2e8f0;
        }
        .form-section:last-of-type {
          border-bottom: none;
        }
        .form-section h4 {
          margin: 0 0 16px 0;
          color: #2d3748;
          font-size: 14px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .modal-large {
          max-width: 600px;
          max-height: 90vh;
          overflow-y: auto;
        }
        .flex-1 { flex: 1; }
        .flex-2 { flex: 2; }
        .currency-select {
          padding: 10px 12px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 14px;
          background: white;
          cursor: pointer;
        }
        .checkbox-group {
          margin-bottom: 16px;
        }
        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          font-size: 14px;
        }
        .checkbox-label input[type="checkbox"] {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }
        textarea {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 14px;
          font-family: inherit;
          resize: vertical;
        }
        .currency-badge {
          display: inline-block;
          margin-left: 6px;
          padding: 2px 6px;
          background: #e2e8f0;
          border-radius: 4px;
          font-size: 10px;
          font-weight: 600;
          color: #4a5568;
        }
        .device-cell {
          min-width: 100px;
        }
        .device-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .device-badge {
          display: inline-block;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
        }
        .device-badge.has-device {
          background: #c6f6d5;
          color: #22543d;
        }
        .device-badge.no-device {
          background: #e2e8f0;
          color: #718096;
        }
        .device-payment {
          font-size: 11px;
          color: #718096;
        }
        .device-total {
          display: block;
          font-size: 11px;
          color: #718096;
        }
        
        /* Payment Status Styles */
        .payment-status-cell {
          min-width: 130px;
        }
        .payment-status-dropdown {
          display: inline-block;
        }
        .payment-status-select {
          padding: 8px 28px 8px 12px;
          border: 2px solid;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          outline: none;
          transition: all 0.2s;
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
          background-repeat: no-repeat;
          background-position: right 8px center;
          background-size: 14px;
          min-width: 110px;
        }
        .payment-status-select:hover {
          transform: scale(1.02);
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        }
        .payment-status-select:focus {
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
        }
        .payment-status-select option {
          padding: 10px;
          font-weight: 500;
        }
        
        /* Payment Status Wrapper */
        .payment-status-wrapper {
          display: flex;
          flex-direction: column;
          gap: 6px;
          align-items: flex-start;
        }
        .payment-status-wrapper > div:first-child {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .btn-add-payment {
          padding: 4px 8px;
          border: 2px solid #6366f1;
          border-radius: 8px;
          background: #eef2ff;
          color: #4f46e5;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }
        .btn-add-payment:hover {
          background: #6366f1;
          color: white;
          transform: scale(1.05);
        }
        .month-paid-amount {
          font-size: 11px;
          color: #059669;
          font-weight: 500;
          background: #d1fae5;
          padding: 2px 8px;
          border-radius: 10px;
        }
        
        /* Payment Modal */
        .modal-small {
          max-width: 400px;
        }
        .payment-hospital-info {
          background: #f1f5f9;
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 16px;
        }
        .hospital-name-display {
          display: block;
          font-weight: 600;
          font-size: 16px;
          color: #1e293b;
          margin-bottom: 4px;
        }
        .subscription-info {
          font-size: 13px;
          color: #64748b;
        }
        .quick-amount-buttons {
          display: flex;
          gap: 8px;
          margin-top: 8px;
        }
        .quick-btn {
          flex: 1;
          padding: 8px 12px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          background: #f8fafc;
          color: #475569;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }
        .quick-btn:hover {
          background: #6366f1;
          color: white;
          border-color: #6366f1;
        }
      `}</style>
    </div>
  );
};

export default Hospitals;
